import { useCallback, useEffect, useMemo, useRef } from "react"
import { debounce } from "lib/debounce"
import { normalizedStringify } from "library/normalized-stringify/index.mjs"
import { ToBeSaved, SaveSkippedUnchanged } from "lib/@components/save-indicator/to-be-saved"
import { persistRecovery, clearRecovery, retryDelay } from "lib/@hooks/save-recovery"

const MAX_RETRIES = 5

export function useSave({ save: suppliedSaveFn, target, id, wait = 1000, deferDirtyRetry = false }, deps = []) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const save = useCallback(suppliedSaveFn, [...deps, target])
    const currentTarget = useRef()
    currentTarget.current = target
    const idRef = useRef(id)
    idRef.current = id

    useEffect(() => {
        window.addEventListener("beforeunload", saveCurrent)
        return () => {
            window.removeEventListener("beforeunload", saveCurrent)
        }

        function saveCurrent() {
            save(currentTarget.current)
        }
    }, [save])

    const isDirty = useRef(false)
    const inFlight = useRef(false)
    const retryTimer = useRef(null)
    const retryCount = useRef(0)
    // Tracks true component unmount only — NOT dep changes.
    // Using empty deps so the cleanup only fires once on unmount, not every
    // time save/doSave changes (which would incorrectly block performSave).
    const unmountedRef = useRef(false)
    const last = useRef(normalizedStringify(target))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const doSave = useCallback(debounce(performSave, wait, { maxWait: 30000 }), [save])
    const saveRef = useRef(save)
    const doSaveRef = useRef(doSave)
    useEffect(() => {
        saveRef.current = save
        doSaveRef.current = doSave
    }, [save, doSave])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
        () => () => {
            unmountedRef.current = true
        },
        []
    )
    useEffect(
        () => () => {
            clearTimeout(retryTimer.current)
            if (isDirty.current) {
                doSaveRef.current.cancel()
                saveRef.current(currentTarget.current)
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )
    return useMemo(() => {
        const result = () => {
            isDirty.current = true
            if (id) ToBeSaved(id).raise(true)
            persistRecovery(id, currentTarget.current)
            doSave()
        }
        result.flush = () => {
            isDirty.current = true
            doSave()
            doSave.flush()
        }
        return result
    }, [doSave, id])

    function performSave(isRetry) {
        clearTimeout(retryTimer.current)
        retryTimer.current = null
        if (!isRetry) retryCount.current = 0

        if (unmountedRef.current) return
        if (inFlight.current) return

        const currentId = idRef.current
        const current = normalizedStringify(currentTarget.current)
        if (current === last.current) {
            isDirty.current = false
            clearRecovery(currentId)
            if (currentId) {
                ToBeSaved(currentId).raise(false)
                SaveSkippedUnchanged(currentId).raise()
            }
            return
        }

        inFlight.current = true
        isDirty.current = false

        let result
        try {
            result = save(currentTarget.current)
        } catch (e) {
            // Synchronous throw — handleError resets inFlight and schedules retry.
            handleError(e)
            return
        }
        if (result != null && typeof result.then === "function") {
            result.then(handleSuccess, handleError)
        } else {
            last.current = current
            inFlight.current = false
            retryCount.current = 0
            clearRecovery(currentId)
            if (isDirty.current) {
                scheduleDirtyRetry()
            } else if (currentId) {
                ToBeSaved(currentId).raise(false)
            }
        }

        function handleSuccess() {
            if (unmountedRef.current) return
            last.current = current
            inFlight.current = false
            retryCount.current = 0
            clearRecovery(currentId)
            if (isDirty.current) {
                scheduleDirtyRetry()
            } else if (currentId) {
                ToBeSaved(currentId).raise(false)
            }
        }

        function scheduleDirtyRetry() {
            if (deferDirtyRetry) {
                doSave()
            } else {
                performSave()
            }
        }

        function handleError(error) {
            if (unmountedRef.current) return
            console.error(error)
            isDirty.current = true
            inFlight.current = false
            if (currentId) ToBeSaved(currentId).raise(error instanceof Error ? error : true)

            retryCount.current++
            if (retryCount.current <= MAX_RETRIES) {
                retryTimer.current = setTimeout(() => performSave(true), retryDelay(retryCount.current))
            }
        }
    }
}
